/**
 * Registration (§13.7 step 2-4).
 *
 * One screen, not a wizard. A four-step flow with a progress bar looks
 * thorough and loses people at step three; this is short enough to see the
 * end of, and KYC is explicitly optional with the cost of skipping it stated
 * rather than hidden.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { useI18n, useT, capabilityLabel, CAPABILITY_LABEL } from '../lib/i18n';
import { STATES } from '../lib/creator';
import { Card, Field, ActionBar } from '../components/Primitives';
import { Icon, type IconName } from '../components/Icon';

/** Who is signing up: a business that does work or owns media, or a creator. */
type Mode = 'business' | 'creator';

const CITIES = [
  'Meerut',
  'Ghaziabad',
  'Noida',
  'Muzaffarnagar',
  'Saharanpur',
  'Lucknow',
  'Kanpur',
  'Agra',
  'Bareilly',
  'Moradabad',
  'Aligarh',
  'Varanasi',
  'Dehradun',
  'Haridwar',
  'Roorkee',
  'Haldwani',
];

/** The capabilities a vendor is most likely to pick, first. */
const CAPABILITY_ORDER = [
  'print_flex',
  'installation',
  'ooh_hoarding',
  'signage_fabrication',
  'print_digital',
  'print_offset',
  'ooh_society',
  'ooh_transit',
  'digital_screen',
  'newspaper',
  'radio',
  'voice_studio',
  'photography',
  'videography',
  'distribution',
  'event_management',
  'ooh_mobile_van',
  'event_venue',
  'cinema',
  'tv_cable',
];

export function RegisterPage() {
  const { t, lang } = useI18n();
  const T = useT();
  const navigate = useNavigate();
  const { refresh, signOut, phone } = useSession();
  // The sign-up form already asked "creator or business"; start on that answer.
  const [mode, setMode] = useState<Mode>(() => {
    try {
      return localStorage.getItem('lulu_partner_join_as') === 'creator'
        ? 'creator'
        : 'business';
    } catch {
      return 'business';
    }
  });
  // Creators are anywhere in India, not just our launch cities: free text.
  const [creatorCity, setCreatorCity] = useState('');
  const [creatorState, setCreatorState] = useState('Uttar Pradesh');

  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [city, setCity] = useState('Meerut');
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [pan, setPan] = useState('');
  const [gstin, setGstin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const creator = mode === 'creator';
  const canSubmit =
    businessName.trim().length >= 2 &&
    contactName.trim().length >= 2 &&
    (creator
      ? creatorCity.trim().length >= 2 && creatorState.length > 0
      : capabilities.length > 0);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/partner/register', {
        businessName: businessName.trim(),
        contactName: contactName.trim(),
        capabilities: creator ? ['creator'] : capabilities,
        baseCity: creator ? creatorCity.trim() : city,
        baseState: creator
          ? creatorState
          : ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani'].includes(city)
            ? 'Uttarakhand'
            : 'Uttar Pradesh',
        serviceCities: [creator ? creatorCity.trim() : city],
        // Empty strings must not reach the API: they fail the PAN pattern and
        // the vendor sees a validation error for a field they left blank.
        ...(pan.trim() ? { pan: pan.trim().toUpperCase() } : {}),
        ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
        languages: [lang, lang === 'hi' ? 'en' : 'hi'],
      });
      await refresh();
      // A new creator lands on their checklist, not on an empty jobs list.
      if (creator) navigate('/creator', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-4" data-sec={creator ? 'creator' : 'jobs'}>
      <header
        className="relative mb-5 overflow-hidden rounded-[26px] p-5 text-white shadow-lift"
        style={{
          backgroundImage: 'linear-gradient(140deg, #0f766e 0%, #6d28d9 60%, #db2777 100%)',
        }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/15"
        />
        <img
          src={`${import.meta.env.BASE_URL}lulu-logo.svg`}
          alt=""
          className="relative h-10 w-10 rounded-xl ring-2 ring-white/40"
        />
        <h1 className="relative mt-4 font-display text-[26px] font-bold leading-tight">
          {creator ? T('क्रिएटर के रूप में जुड़ें', 'Join as a creator') : t('register.title')}
        </h1>
        <p className="relative mt-1 text-sm text-white/85">
          {creator
            ? T(
                'अपना मीडिया किट बनाएँ, दाम तय करें, ब्रांड से सीधे पैसे वाली डील पाएँ।',
                'Build your media kit, set your rates, and get paid deals from brands directly.',
              )
            : t('register.subtitle')}
        </p>
        {phone && <p className="tnum relative mt-2 text-xs text-white/70">{phone}</p>}
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-4"
      >
        <div
          className="grid grid-cols-2 gap-2.5"
          role="radiogroup"
          aria-label={T('आप कौन हैं?', 'Who are you?')}
        >
          {(
            [
              [
                'business',
                'jobs',
                T('मेरा बिज़नेस है', 'I run a business'),
                T(
                  'प्रिंटिंग, साइट, मीडिया, इंस्टॉलेशन…',
                  'Printing, sites, media, installation…',
                ),
              ],
              [
                'creator',
                'sparkle',
                T('मैं क्रिएटर / इन्फ़्लुएंसर हूँ', 'I am a creator / influencer'),
                T('Instagram, YouTube, UGC…', 'Instagram, YouTube, UGC…'),
              ],
            ] as Array<[Mode, IconName, string, string]>
          ).map(([k, icon, label, sub]) => {
            const on = mode === k;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setMode(k)}
                data-sec={k === 'creator' ? 'creator' : 'jobs'}
                className={`card flex flex-col items-start gap-2 p-3.5 text-left transition ${
                  on ? '!border-2' : 'opacity-80'
                }`}
                style={
                  on
                    ? { borderColor: 'var(--sec-ink)', background: 'var(--sec-wash)' }
                    : undefined
                }
              >
                <span className="icon-tile h-9 w-9">
                  <Icon name={icon} className="h-5 w-5" />
                </span>
                <span className="text-[15px] font-bold leading-tight">{label}</span>
                <span className="text-xs leading-snug text-muted">{sub}</span>
              </button>
            );
          })}
        </div>

        <Card className="space-y-4">
          <Field
            label={
              creator
                ? T('क्रिएटर / चैनल का नाम', 'Creator or channel name')
                : t('register.businessName')
            }
            hint={
              creator
                ? T('जिस नाम से लोग आपको जानते हैं', 'The name people know you by')
                : undefined
            }
          >
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              autoComplete={creator ? 'nickname' : 'organization'}
              placeholder={creator ? 'Priya Cooks' : 'Sharma Flex Printers'}
            />
          </Field>

          <Field label={t('register.contactName')}>
            <input
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              autoComplete="name"
            />
          </Field>

          {creator ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('register.city')}>
                <input
                  value={creatorCity}
                  onChange={(e) => setCreatorCity(e.target.value)}
                  autoComplete="address-level2"
                  list="creator-cities"
                />
                <datalist id="creator-cities">
                  {CITIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </Field>
              <Field label={t('register.state')}>
                <select value={creatorState} onChange={(e) => setCreatorState(e.target.value)}>
                  {STATES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          ) : (
            <Field label={t('register.city')}>
              <select value={city} onChange={(e) => setCity(e.target.value)}>
                {CITIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </Card>

        {creator && (
          <Card className="space-y-2">
            {[
              T(
                'अपना मीडिया किट बनाएँ — फ़ोटो, चैनल, बेहतरीन काम',
                'Build your media kit — photo, channels, best work',
              ),
              T(
                'अपने पैकेज के दाम लगाएँ (सही दाम का सुझाव भी मिलेगा)',
                'Price your packages (with a fair-rate suggestion)',
              ),
              T(
                'ब्रांड पहले पेमेंट करता है, फिर आप काम शुरू करते हैं',
                'Brands pay first; you start work after that',
              ),
            ].map((line) => (
              <p key={line} className="flex items-start gap-2 text-sm">
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
                >
                  <Icon name="check" className="h-3 w-3" strokeWidth={3} />
                </span>
                {line}
              </p>
            ))}
          </Card>
        )}

        <Card className={creator ? 'hidden' : ''}>
          <p className="field-label">{t('register.capabilities')}</p>
          <p className="mb-3 text-xs text-muted">{t('register.capabilitiesHint')}</p>

          <div className="flex flex-wrap gap-2">
            {CAPABILITY_ORDER.filter((c) => CAPABILITY_LABEL[c]).map((c) => {
              const on = capabilities.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setCapabilities((prev) =>
                      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
                    )
                  }
                  className={`min-h-touch rounded-full border px-4 text-sm ${
                    on
                      ? 'border-brand bg-brand/15 font-medium'
                      : 'border-line bg-panel text-muted'
                  }`}
                >
                  {capabilityLabel(c, lang)}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="space-y-4">
          <p className="field-label">{t('register.kyc')}</p>

          <Field label={`${t('register.pan')} (${t('common.optional')})`}>
            <input
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              maxLength={10}
              placeholder="ABCPK1234E"
              className="tnum tracking-wider"
            />
          </Field>

          <Field label={`${t('register.gstin')} (${t('common.optional')})`}>
            <input
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              maxLength={15}
              className="tnum tracking-wider"
            />
          </Field>

          {/* Said plainly and up front. A vendor who discovers the 20% on their
              first payout believes they were cheated, and they are not wrong to
              be angry about learning it then. */}
          {!pan.trim() && (
            <p className="rounded-lg bg-live/10 p-3 text-xs text-live">
              {t('register.panWarning')}
            </p>
          )}
        </Card>

        {error && <p className="text-sm text-bad">{error}</p>}

        <ActionBar>
          <button type="submit" className="btn-primary" disabled={!canSubmit || busy}>
            {busy ? t('register.submitting') : t('register.submit')}
          </button>
          <button
            type="button"
            className="btn-quiet mt-3 block w-full pb-1 text-center text-sm"
            onClick={() => void signOut()}
          >
            {t('profile.signOut')}
          </button>
        </ActionBar>
      </form>
    </div>
  );
}
